// Customer 360° right-column view for the Orders module.
// Extracted from the deprecated WhatsApp module — stripped of Baileys/LID
// logic and Call Center dependencies; purely Shopify + Returns + Service.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShoppingBag, Mail, MapPin, Phone, ExternalLink, User, Check,
  Calendar, Package, Wrench, RotateCcw, TrendingUp, Truck,
  ShoppingCart, ChevronDown,
} from "lucide-react";
import { fmtDate, fmtShort, fmtRelative } from "@/lib/format";

export default function CustomerPanel({ conversation, customer, loading }) {
  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
        <User className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">Selectează o comandă pentru a vedea profilul clientului.</p>
      </div>
    );
  }

  const phone = conversation.displayPhone || conversation.phone || "";
  const shopify = customer?.shopify;
  const found = !!shopify?.found;
  const fullName = found
    ? `${shopify.first_name || ""} ${shopify.last_name || ""}`.trim() || conversation.contactName || phone
    : conversation.contactName || "Necunoscut";

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="orders-customer-panel">
      {/* HERO: contact card */}
      <div className="p-4 border-b border-border">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
          Profil client
        </div>

        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
              {(fullName || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{fullName}</div>
            {phone && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 font-mono mt-0.5">
                <Phone className="h-3 w-3" />
                {phone}
              </div>
            )}
          </div>
          {found && (
            <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-[10px] shrink-0">
              <Check className="h-2.5 w-2.5 mr-1" /> Client
            </Badge>
          )}
        </div>

        {found && (
          <div className="space-y-1.5 text-xs mt-3">
            {shopify.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{shopify.email}</span>
              </div>
            )}
            {shopify.address?.city && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                <span>
                  {[shopify.address.address1, shopify.address.city, shopify.address.zip]
                    .filter((x) => x && x !== "-")
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {loading && !customer && (
        <div className="p-4 space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {found && (
        <div className="p-4 border-b border-border space-y-3">
          <SectionLabel>Istoric Shopify</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <StatCard icon={ShoppingCart} label="Comenzi" value={shopify.orders_count || 0} />
            <StatCard icon={TrendingUp} label="Total" value={`${Number(shopify.total_spent || 0).toFixed(0)} ${shopify.currency || "RON"}`} />
            <StatCard icon={Calendar} label="Prima comandă" value={shopify.first_order_at ? fmtShort(shopify.first_order_at) : "—"} small />
            <StatCard icon={Calendar} label="Ultima comandă" value={shopify.last_order_at ? fmtShort(shopify.last_order_at) : "—"} small />
          </div>
          {shopify.admin_url && (
            <a
              href={shopify.admin_url}
              target="_blank"
              rel="noreferrer"
              className="w-full text-[10px] uppercase tracking-widest font-semibold text-primary hover:text-primary/80 transition-colors flex items-center justify-center gap-1 py-1"
              data-testid="orders-customer-shopify-link"
            >
              Vezi clientul în Shopify
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      )}

      {shopify?.recent_orders?.length > 0 && (
        <div className="p-4 border-b border-border">
          <SectionLabel>Comenzi recente <span className="text-muted-foreground/60 ml-1">({shopify.recent_orders.length})</span></SectionLabel>
          <div className="space-y-2">
            {shopify.recent_orders.map((o) => <OrderCard key={o.id} order={o} />)}
          </div>
        </div>
      )}

      {customer && (
        <div className="p-4 border-b border-border">
          <SectionLabel>Retururi <span className="text-muted-foreground/60 ml-1">({customer.returns_count || 0})</span></SectionLabel>
          {customer.returns?.length > 0 ? (
            <div className="space-y-2">{customer.returns.map((r) => <ReturnCard key={r.id} ret={r} />)}</div>
          ) : (
            <EmptyMini icon={RotateCcw} text="Nu există retururi pentru acest client." />
          )}
        </div>
      )}

      {customer && (
        <div className="p-4 border-b border-border">
          <SectionLabel>Service <span className="text-muted-foreground/60 ml-1">({customer.service_count || 0})</span></SectionLabel>
          {customer.service?.length > 0 ? (
            <div className="space-y-2">{customer.service.map((s) => <ServiceCard key={s.id} svc={s} />)}</div>
          ) : (
            <EmptyMini icon={Wrench} text="Nu există cereri service pentru acest client." />
          )}
        </div>
      )}

      {customer && !found && (
        <div className="p-4 border-b border-border">
          <EmptyMini icon={ShoppingBag} text="Niciun client Shopify găsit pentru acest număr." />
        </div>
      )}
    </div>
  );
}

// ---------- Subcomponents ----------
function SectionLabel({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center justify-between">
      <span>{children}</span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, small }) {
  return (
    <Card className="p-2.5 bg-card border-border">
      <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground uppercase tracking-widest">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className={`font-display font-bold mt-1 ${small ? "text-sm" : "text-lg"}`}>
        {value}
      </div>
    </Card>
  );
}

function EmptyMini({ icon: Icon, text }) {
  return (
    <Card className="p-3 bg-secondary/20 border-dashed border-border/70">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
        <span>{text}</span>
      </div>
    </Card>
  );
}

function statusColor(s) {
  if (!s) return "#71717a";
  if (/fulfill|delivered|paid|complete|finaliz/i.test(s)) return "#10b981";
  if (/pending|process|await|nou|diagnos/i.test(s)) return "#f59e0b";
  if (/cancel|void|refus|respins/i.test(s)) return "#ef4444";
  return "#71717a";
}

function OrderCard({ order }) {
  const [expanded, setExpanded] = useState(false);
  const finStatus = order.financial_status;
  const fulStatus = order.fulfillment_status;
  const tracking = (order.tracking && order.tracking[0]) || null;
  const items = order.items || [];
  return (
    <Card className="p-3 bg-card border-border hover:border-primary/30 transition-colors" data-testid={`orders-customer-order-${order.name}`}>
      <div
        className={`flex items-start justify-between gap-2 mb-1.5 ${items.length ? "cursor-pointer" : ""}`}
        onClick={() => items.length && setExpanded((v) => !v)}
      >
        <div className="min-w-0">
          <div className="font-semibold text-sm font-mono flex items-center gap-1">
            {order.name}
            {items.length > 0 && (
              <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {order.created_at ? fmtDate(order.created_at) : "—"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm font-semibold">
            {Number(order.total).toFixed(2)} {order.currency}
          </div>
        </div>
      </div>
      {expanded && items.length > 0 && (
        <div className="mb-2 rounded-md bg-secondary/40 border border-border/60 divide-y divide-border/60">
          {items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px]">
              <span className="truncate">{it.title}</span>
              <span className="shrink-0 text-muted-foreground font-mono">
                {it.quantity} × {Number(it.price).toFixed(2)} {it.currency}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        {finStatus && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-current/30"
            style={{ color: statusColor(finStatus), background: statusColor(finStatus) + "12" }}>
            {finStatus}
          </Badge>
        )}
        {fulStatus && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-current/30"
            style={{ color: statusColor(fulStatus), background: statusColor(fulStatus) + "12" }}>
            {fulStatus}
          </Badge>
        )}
      </div>
      {tracking?.awb && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-2">
          <Truck className="h-3 w-3" />
          <span className="font-mono">{tracking.awb}</span>
          {tracking.courier && <span>· {tracking.courier}</span>}
          {tracking.url && (
            <a href={tracking.url} target="_blank" rel="noreferrer" className="ml-auto text-primary hover:underline">
              track
            </a>
          )}
        </div>
      )}
      {order.admin_url && (
        <button
          className="w-full text-[10px] uppercase tracking-widest font-semibold text-primary hover:text-primary/80 transition-colors flex items-center justify-center gap-1 py-1"
          onClick={() => window.open(order.admin_url, "_blank")}
        >
          Vezi comanda
          <ExternalLink className="h-2.5 w-2.5" />
        </button>
      )}
    </Card>
  );
}

function ReturnCard({ ret }) {
  return (
    <Card className="p-3 bg-card border-border hover:border-primary/30 transition-colors" data-testid={`orders-customer-return-${ret.return_number}`}>
      <Link to={`/retururi/${ret.id}`} className="block">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0">
            <div className="font-semibold text-sm font-mono">{ret.return_number}</div>
            <div className="text-[10px] text-muted-foreground">
              Comandă #{ret.order_number} · {fmtRelative(ret.created_at)}
            </div>
          </div>
          {ret.status && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 shrink-0"
              style={{ color: ret.status.color, background: ret.status.color + "12", borderColor: ret.status.color + "30" }}>
              {ret.status.name}
            </Badge>
          )}
        </div>
        {ret.items_preview?.length > 0 && (
          <div className="text-[11px] text-muted-foreground truncate">
            <Package className="h-3 w-3 inline mr-1" />
            {ret.items_preview.join(" · ")}
          </div>
        )}
        {ret.awb && (
          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
            <Truck className="h-3 w-3" />
            <span className="font-mono">{ret.awb}</span>
            {ret.courier && <span>· {ret.courier}</span>}
          </div>
        )}
      </Link>
    </Card>
  );
}

function ServiceCard({ svc }) {
  return (
    <Card className="p-3 bg-card border-border hover:border-primary/30 transition-colors" data-testid={`orders-customer-service-${svc.service_number}`}>
      <Link to={`/service/${svc.id}`} className="block">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0">
            <div className="font-semibold text-sm font-mono">{svc.service_number}</div>
            <div className="text-[10px] text-muted-foreground">
              Comandă #{svc.order_number} · {fmtRelative(svc.created_at)}
            </div>
          </div>
          {svc.status && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 shrink-0"
              style={{ color: svc.status.color, background: svc.status.color + "12", borderColor: svc.status.color + "30" }}>
              {svc.status.name}
            </Badge>
          )}
        </div>
        {svc.items_preview?.length > 0 && (
          <div className="text-[11px] text-muted-foreground truncate">
            <Package className="h-3 w-3 inline mr-1" />
            {svc.items_preview.join(" · ")}
          </div>
        )}
        {svc.problem_type && (
          <div className="text-[11px] text-amber-600 mt-1 truncate">
            <Wrench className="h-3 w-3 inline mr-1" />
            {svc.problem_type}
          </div>
        )}
      </Link>
    </Card>
  );
}
